import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Plus, Home, CheckCircle, Clock, Building } from 'lucide-react';
import { Button, TableHeader, CardStats, ConfirmModal, SearchInput, Pagination, ExportDropdown } from '@components/ui';
import { TablePageSkeleton } from '@components/skeleton';
import { usePagination, useExportExcel } from '@hooks';
import { getApartmentsByConstructionId, getApartmentStats } from '@mocks/daireler';
import DaireTablosu from './DaireTablosu';
import DaireModal from './DaireModal';


export default function Apartments() {
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);
    const constructionId = showAllConstructions ? null : selectedConstruction?.id;

    const [modalOpen, setModalOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedApartment, setSelectedApartment] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    // Demo: Simüle edilmiş yükleme gecikmesi (1.2s)
    const [isLoading, setIsLoading] = useState(true);
    useEffect(() => {
        const timer = setTimeout(() => setIsLoading(false), 1200);
        return () => clearTimeout(timer);
    }, []);

    const allApartments = getApartmentsByConstructionId(constructionId);
    const stats = getApartmentStats(constructionId);

    // Export
    const { exportToExcel, isExporting } = useExportExcel();

    const handleExportExcel = () => {
        const statusMap = { sold: 'Satıldı', reserved: 'Rezerve', available: 'Boş' };
        exportToExcel({
            data: apartments.map(apt => ({
                'Daire No': apt.number,
                'Blok': apt.block,
                'Kat': apt.floor,
                'Oda Sayısı': apt.rooms,
                'm²': apt.area,
                'Fiyat': apt.price,
                'Durum': statusMap[apt.status] || apt.status,
                'Sahip': apt.owner || '-',
            })),
            columns: [
                { header: 'Daire No', key: 'Daire No' },
                { header: 'Blok', key: 'Blok' },
                { header: 'Kat', key: 'Kat' },
                { header: 'Oda Sayısı', key: 'Oda Sayısı' },
                { header: 'm²', key: 'm²' },
                { header: 'Fiyat', key: 'Fiyat', format: 'currency' },
                { header: 'Durum', key: 'Durum' },
                { header: 'Sahip', key: 'Sahip' },
            ],
            fileName: 'daireler',
            sheetName: 'Daireler',
        });
    };

    // Filter apartments based on search query
    const apartments = allApartments.filter(apt => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            apt.number?.toLowerCase().includes(query) ||
            apt.block?.toLowerCase().includes(query) ||
            apt.owner?.toLowerCase().includes(query) ||
            apt.status?.toLowerCase().includes(query)
        );
    });

    // Pagination
    const {
        paginatedData,
        currentPage,
        totalPages,
        totalItems,
        pageSize,
        goToPage,
        resetPage,
    } = usePagination(apartments, 10);

    // Reset pagination when search changes
    useEffect(() => {
        resetPage();
    }, [searchQuery]);


    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 0,
        }).format(amount);
    };

    const handleAdd = () => {
        setSelectedApartment(null);
        setModalOpen(true);
    };

    const handleEdit = (apartment) => {
        setSelectedApartment(apartment);
        setModalOpen(true);
    };

    const handleDelete = (apartment) => {
        setSelectedApartment(apartment);
        setDeleteModalOpen(true);
    };

    const confirmDelete = () => {
        console.log('Daire silindi:', selectedApartment);
        toast.success(`${selectedApartment?.number} numaralı daire başarıyla silindi`);
        setDeleteModalOpen(false);
        setSelectedApartment(null);
    };

    const handleSubmit = (data) => {
        console.log('Daire kaydedildi:', data);
        if (selectedApartment) {
            toast.success(`${data.number} numaralı daire başarıyla güncellendi`);
        } else {
            toast.success(`${data.number} numaralı daire başarıyla eklendi`);
        }
    };

    // Loading skeleton
    if (isLoading) {
        return <TablePageSkeleton statsCount={6} tableRows={10} tableColumns={10} />;
    }

    return (
        <div className="p-4 md:p-6 space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <CardStats

                    title="Toplam Daire"
                    value={stats.total}
                    icon={Home}
                    color="primary"
                    size="sm"
                />
                <CardStats
                    title="Satılan"
                    value={stats.sold}
                    icon={CheckCircle}
                    color="success"
                    size="sm"
                />
                <CardStats
                    title="Rezerve"
                    value={stats.reserved}
                    icon={Clock}
                    color="warning"
                    size="sm"
                />
                <CardStats
                    title="Boş"
                    value={stats.available}
                    icon={Building}
                    color="tertiary"
                    size="sm"
                />
                <CardStats
                    title="Toplam Değer"
                    value={formatCurrency(stats.totalValue)}
                    icon={Home}
                    color="info"
                    size="sm"
                />
                <CardStats
                    title="Tahsil Edilen"
                    value={formatCurrency(stats.totalPaid)}
                    icon={CheckCircle}
                    color="success"
                    size="sm"
                />
            </div>

            {/* Table Header */}
            <TableHeader
                title="Daireler"
                subtitle={showAllConstructions ? 'Tüm inşaatlar' : selectedConstruction?.name}
            >
                <SearchInput
                    value={searchQuery}
                    onChange={setSearchQuery}
                    placeholder="Daire, blok veya sahip ara..."
                    size="md"
                />
                <ExportDropdown
                    onExportExcel={handleExportExcel}
                    isExporting={isExporting}
                />
                <Button
                    variant="primary"
                    icon={<Plus size={18} />}
                    onClick={handleAdd}
                >
                    Daire Ekle
                </Button>
            </TableHeader>


            {/* Table */}
            <DaireTablosu
                data={paginatedData}
                onEdit={handleEdit}
                onDelete={handleDelete}
            />

            {/* Pagination */}
            <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={totalItems}
                pageSize={pageSize}
                onPageChange={goToPage}
            />


            {/* Add/Edit Modal */}
            <DaireModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                onSubmit={handleSubmit}
                apartment={selectedApartment}
                constructionId={constructionId}
            />

            {/* Delete Confirm Modal */}
            <ConfirmModal
                isOpen={deleteModalOpen}
                onClose={() => setDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                title="Daireyi Sil"
                message={`"${selectedApartment?.number}" numaralı daireyi silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`}
                confirmText="Evet, Sil"
                variant="danger"
            />
        </div>
    );
}
